import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';

const MAX_POINTS = 999;
const MAX_DIGITS = String(MAX_POINTS).length;

const HELPER_TEXT = 'Enter a whole number. Decimal values are not allowed.';
const DECIMAL_ERROR = 'Decimal values are not allowed. Enter a whole number.';
const DIGITS_ERROR = `Only whole numbers from 0 to ${MAX_POINTS} are allowed.`;

interface StoryPointsInputProps {
  value: number | null;
  /** Called with a whole number 0–999, or null when cleared — only when it changed. */
  onCommit: (points: number | null) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  /** A small box for a table cell: no helper text, and a rejected character is explained in a toast. */
  compact?: boolean;
}

/**
 * A story's estimate. The value is committed on blur or Enter; Escape puts
 * back the last saved value. Only digits can be typed or pasted, and a
 * rejected character (e.g. a decimal point) is explained right under the field.
 */
const StoryPointsInput: React.FC<StoryPointsInputProps> = ({
  value,
  onCommit,
  disabled = false,
  placeholder = 'Not estimated',
  className = '',
  compact = false,
}) => {
  const [draft, setDraft] = useState(value === null ? '' : String(value));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDraft(value === null ? '' : String(value));
  }, [value]);

  const handleChange = (raw: string) => {
    const digits = raw.replace(/\D/g, '');
    let next: string | null = null;
    if (digits !== raw) next = /[.,]/.test(raw) ? DECIMAL_ERROR : DIGITS_ERROR;
    else if (digits.length > MAX_DIGITS) next = DIGITS_ERROR;
    setError(next);
    // A cell has no room for the message; one toast id keeps repeated keystrokes from stacking.
    if (next && compact) toast.error(next, { id: 'story-points-input' });
    setDraft(digits.slice(0, MAX_DIGITS));
  };

  const commit = () => {
    const next = draft === '' ? null : Number(draft);
    if (next !== value) onCommit(next);
  };

  return (
    <div className={compact ? 'inline-block' : undefined}>
      <input
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        value={draft}
        placeholder={placeholder}
        disabled={disabled}
        aria-invalid={!!error}
        // Keeps a click from opening the row or starting a row drag.
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        onChange={(e) => handleChange(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === 'Enter') e.currentTarget.blur();
          if (e.key === 'Escape') {
            setDraft(value === null ? '' : String(value));
            setError(null);
            // Blurring after the reset commits nothing, since it matches the saved value.
            requestAnimationFrame(() => (e.target as HTMLInputElement).blur());
          }
        }}
        className={`${compact ? 'w-12 h-7 px-1.5 text-center text-xs' : 'w-full h-10 px-3 text-sm'} rounded-lg border bg-primary-variant text-neutral placeholder:text-neutral-medium focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed ${
          error ? 'border-red-500 focus:border-red-500' : 'border-primary-subtle focus:border-[#8B5CF6]'
        } ${className}`}
      />
      {/* Not shown when the field is disabled (e.g. a non-story item can't be estimated). */}
      {!disabled && !compact && (
        <p className={`mt-1.5 text-xs ${error ? 'text-red-400' : 'text-neutral-medium'}`}>
          {error ?? HELPER_TEXT}
        </p>
      )}
    </div>
  );
};

export default StoryPointsInput;
